import { useMemo } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { db, type Item, type Room } from '../db/schema';
import { matchItem, normalise, type Match } from './match';

/**
 * Everything, grouped by room.
 *
 * The whole library is read once and filtered in memory. Two hundred items of
 * small records — no blobs, they're referenced by id — is nothing to hold, and
 * a query per keystroke would be slower than the filter it was avoiding.
 */

export type Hit = {
  item: Item;
  match: Match;
};

export type Group = {
  room: Room;
  hits: Hit[];
};

export type Results = {
  groups: Group[];
  count: number;
  /** Undefined until the first read lands. */
  ready: boolean;
};

export function useResults(query: string): Results {
  const library = useLiveQuery(async () => {
    const [rooms, items] = await Promise.all([db.rooms.orderBy('sort').toArray(), db.items.toArray()]);
    return { rooms, items };
  }, []);

  return useMemo(() => {
    const needle = normalise(query);
    if (library === undefined) return { groups: [], count: 0, ready: false };
    if (needle === '') return { groups: [], count: 0, ready: true };

    const hits = new Map<string, Hit[]>();
    for (const item of library.items) {
      const match = matchItem(item, needle);
      if (match === undefined) continue;

      const list = hits.get(item.roomId);
      if (list === undefined) hits.set(item.roomId, [item].map((each) => ({ item: each, match })));
      else list.push({ item, match });
    }

    // Rooms in rail order, items newest first inside each — the same direction
    // a board reads, so a result list isn't a third arrangement to learn.
    const groups = library.rooms.flatMap((room) => {
      const found = hits.get(room.id);
      if (found === undefined || found.length === 0) return [];
      return [{ room, hits: [...found].sort((a, b) => b.item.sort - a.item.sort) }];
    });

    return {
      groups,
      count: groups.reduce((total, group) => total + group.hits.length, 0),
      ready: true,
    };
  }, [library, query]);
}
